// src/pages/Home.js

import React, { useState, useContext, useEffect } from 'react';
import {
  Typography,
  Grid,
  Box,
  Button,
  Fade,
  Slide,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/system';
import Navbar from '../components/Navbar';
import SubscriptionCard from '../components/SubscriptionCard';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';
import SubscriptionPayment from '../components/SubscriptionPayment';
import SubscriptionModal from '../components/SubscriptionModal';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ServerSelector from '../components/serverSetup/ServerSelector';
import backgroundImage from '../assets/mindguard_home.jpg';
import mindguardLogo from '../assets/mindguard_logo_final.png';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';

const BackgroundBox = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center top',
  backgroundRepeat: 'no-repeat',
  backgroundAttachment: 'fixed',
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  overflowX: 'hidden',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1,
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  maxWidth: '1200px',
  margin: '0 auto',
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(8),
  padding: theme.spacing(6, 4),
  borderRadius: '16px',
  boxShadow: 'none', // Removed shadow to match the transparent background
  animation: 'fadeIn 2s ease-in-out',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
}));

const FeaturesSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
}));

const PricingSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark transparent color to match Option 3
  borderRadius: '16px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', // Light shadow for better integration
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 4px 25px rgba(0, 0, 0, 0.3)', // Slightly darker shadow on hover
  },
  height: '100%',
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  marginBottom: theme.spacing(2),
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const Home = () => {
  const { user, isSubscribed, accessToken, setGuildId, isSubscriptionFlow, setIsSubscriptionFlow } = useContext(AuthContext);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null); // Currently selected plan
  const [openGuildSelectionModal, setOpenGuildSelectionModal] = useState(false);
  const [guilds, setGuilds] = useState([]);
  const navigate = useNavigate();

  const subscriptionOptions = [
    {
      title: 'Basic',
      amount: 1,
      collections: 1,
      features: ['Manage 1 collection', 'Basic Support'],
    },
    {
      title: 'Pro',
      amount: 2.5,
      collections: 5,
      features: ['Manage up to 5 collections', 'Priority Support', 'Advanced Features'],
    },
    {
      title: 'Premium',
      amount: 5,
      collections: 10,
      features: ['Manage up to 10 collections', '24/7 Support', 'All Features Included'],
    },
  ];

  const handleSubscribe = (option) => {
    setSubscriptionPlan(option);
    setSelectedPlan(option);
    localStorage.setItem('selectedPlan', JSON.stringify(option));

    if (user) {
      // User is authenticated, proceed to guild selection
      setOpenGuildSelectionModal(true);
    } else {
      // User is not authenticated, initiate Discord OAuth2 login
      setIsSubscriptionFlow(true);
      initiateDiscordLogin('subscribe');
    }
  };

  const initiateDiscordLogin = (state) => {
    const clientId = process.env.REACT_APP_DISCORD_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_DISCORD_REDIRECT_URI_ADMIN; // Ensure this matches OAuthCallback route
    const responseType = 'code';
    const scope = 'identify guilds';
    const stateParam = state; // e.g., 'subscribe'

    if (!clientId || !redirectUri) {
      console.error('RedirectHandler: REACT_APP_DISCORD_CLIENT_ID or REACT_APP_DISCORD_REDIRECT_URI_ADMIN is not defined.');
      return;
    }

    const discordAuthURL = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=${responseType}&scope=${encodeURIComponent(scope)}&state=${stateParam}`;

    window.location.href = discordAuthURL;
  };

  useEffect(() => {
    if (user && openGuildSelectionModal) {
      // Fetch guilds when the modal is opened
      fetchGuilds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, openGuildSelectionModal]);

  const fetchGuilds = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setGuilds(response.data);
    } catch (error) {
      console.error('Home: Error fetching guilds:', error);
      alert('Failed to fetch guilds. Please try again.');
      setOpenGuildSelectionModal(false);
    }
  };

  const handleGuildSelect = async (guildId) => {
    setGuildId(guildId);
    localStorage.setItem('guildId', guildId);

    // Now check subscription status
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/server-config?guildId=${guildId}`
      );

      const serverConfig = response.data;
      const now = new Date();
      const subscriptionExpiresAt = new Date(serverConfig.subscriptionExpiresAt);

      if (subscriptionExpiresAt > now) {
        // Subscription is active
        setOpenGuildSelectionModal(false);
        navigate('/setup', { replace: true });
      } else {
        // Subscription is inactive
        // Proceed with subscription purchase flow
        setOpenGuildSelectionModal(false);
        // Optionally, notify the user about the expired subscription
        alert('Your subscription has expired. Please renew to continue.');
        navigate('/purchase', { replace: true });
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // Server config not found, treat as no subscription
        setOpenGuildSelectionModal(false);
        navigate('/purchase', { replace: true }); // Redirect to purchase
      } else {
        console.error('Home: Error checking subscription status:', error);
        alert('An error occurred. Please try again.');
        setOpenGuildSelectionModal(false);
      }
    }
  };

  // Handle redirect after OAuth2 (post-authentication)
  useEffect(() => {
    if (isSubscriptionFlow && user && isSubscribed) {
      // If the user was in subscription flow and is now subscribed, redirect to purchase or setup
      const storedPlan = localStorage.getItem('selectedPlan');
      if (storedPlan) {
        const plan = JSON.parse(storedPlan);
        setSelectedPlan(plan);
        localStorage.removeItem('selectedPlan'); // Clean up after use
        navigate('/purchase'); // Redirect to purchase with the selected plan
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSubscribed, isSubscriptionFlow]);

  return (
    <BackgroundBox>
      {/* Overlay */}
      <Overlay />

      {/* Content */}
      <ContentWrapper>
        {/* Navbar */}
        <Navbar />

        {/* Hero Section with Logo and Title */}
        <Fade in={true} timeout={1000}>
          <HeroSection>
            <LogoContainer>
              <img src={mindguardLogo} alt="MINDguard Logo" style={{ width: '200px', height: 'auto' }} />
              <Typography variant="h3" sx={{ color: '#A1D1F0', marginTop: 2, fontWeight: 700 }}>
                MINDguard
              </Typography>
            </LogoContainer>
            <Typography variant="h5" gutterBottom sx={{ animation: 'slideInUp 1.5s ease-out' }}>
              Secure Your Discord Server with Seamless NFT Verification
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 700, margin: '0 auto', mb: 4, animation: 'fadeIn 2s ease-out' }}>
              At MINDguard, we empower projects to secure role verifications in Discord effortlessly. Our affordable pricing supports builders on-chain, ensuring your community thrives without locking up funds in expensive verification tools.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{
                marginTop: 4,
                boxShadow: '0 4px 15px rgba(154, 192, 205, 0.4)',
                animation: 'pulse 2s infinite',
              }}
              onClick={() => {
                handleSubscribe(subscriptionOptions[0]);
              }}
            >
              Get Started
            </Button>
          </HeroSection>
        </Fade>

        {/* Features Section */}
        <FeaturesSection>
          <Typography variant="h2" gutterBottom sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 700 }}>
            Why Choose MINDguard?
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {/* Feature 1 */}
            <Grid item xs={12} sm={6} md={4}>
              <Slide direction="up" in={true} timeout={1000} style={{ transitionDelay: '300ms' }}>
                <FeatureCard>
                  <CardContent>
                    <FeatureIcon>
                      <SecurityIcon fontSize="large" sx={{ color: '#fff' }} />
                    </FeatureIcon>
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Robust Security
                    </Typography>
                    <Typography variant="body1">
                      Ensure your Discord server is protected with advanced security measures tailored for NFT verifications.
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Slide>
            </Grid>

            {/* Feature 2 */}
            <Grid item xs={12} sm={6} md={4}>
              <Slide direction="up" in={true} timeout={1000} style={{ transitionDelay: '500ms' }}>
                <FeatureCard>
                  <CardContent>
                    <FeatureIcon>
                      <PeopleIcon fontSize="large" sx={{ color: '#fff' }} />
                    </FeatureIcon>
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Community Support
                    </Typography>
                    <Typography variant="body1">
                      Join a vibrant community that supports builders and innovators on Solana, fostering growth and collaboration.
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Slide>
            </Grid>

            {/* Feature 3 */}
            <Grid item xs={12} sm={6} md={4}>
              <Slide direction="up" in={true} timeout={1000} style={{ transitionDelay: '700ms' }}>
                <FeatureCard>
                  <CardContent>
                    <FeatureIcon>
                      <IntegrationInstructionsIcon fontSize="large" sx={{ color: '#fff' }} />
                    </FeatureIcon>
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Seamless Integration
                    </Typography>
                    <Typography variant="body1">
                      Easily integrate MINDguard into your Discord server with our intuitive setup process.
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Slide>
            </Grid>
          </Grid>
        </FeaturesSection>

        {/* Pricing Section */}
        <PricingSection>
          <Typography variant="h2" gutterBottom sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 700 }}>
            Choose Your Plan
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {subscriptionOptions.map((option, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <SubscriptionCard option={option} onSubscribe={handleSubscribe} />
              </Grid>
            ))}
          </Grid>
        </PricingSection>

        {/* Subscription Modal */}
        {subscriptionPlan && !user && (
          <SubscriptionModal
            open={!user} // Open modal only if user is not authenticated
            handleClose={() => setSubscriptionPlan(null)}
            subscriptionPlan={subscriptionPlan}
          />
        )}

        {/* Payment Flow */}
        {selectedPlan && user && (
          <SubscriptionPayment
            subscription={selectedPlan}
            onClose={() => setSelectedPlan(null)} // To handle closing the payment flow
          />
        )}

        {/* Footer */}
        <Footer />
      </ContentWrapper>

      {/* Guild Selection Modal */}
      {openGuildSelectionModal && (
        <ServerSelector
          open={openGuildSelectionModal}
          guilds={guilds}
          handleClose={() => setOpenGuildSelectionModal(false)}
          handleGuildSelect={handleGuildSelect}
        />
      )}
    </BackgroundBox>
  );
};

export default Home;

